import { Box, Paper, Stack, Typography } from '@mui/material';
import { Fragment, ReactNode } from 'react';
import { Divider } from '../../atoms/index.ts';
import FormActionButton from './FormActionButton.tsx';
import { LoadingButton } from '~/components/private/index.ts';

export type FormMode = 'add' | 'edit';

export interface FormSection {
  title: string;
  key: string;
  content: ReactNode;
  actionSlot?: ReactNode;
  allowAdd?: boolean;
  disableEdit?: boolean;
  hasSectionValues?: boolean;
  onEditCancel?: () => void;
  shouldRemainInEditMode?: boolean;
}

export interface FormSectionLayoutProps {
  width?: string;
  padding?: string;
  sections: FormSection[];
  formMode?: FormMode;
  editKey: string | null;
  isSaving?: boolean;
  onEdit?: (editKey: string | null) => void;
  onSave: (sectionKey: string) => void;
  dataTestId?: string;
  elevation?: number;
  addText?: string;
  backgroundColor?: string;
  /** @description disables all controls inserted by this component */
  disabled?: boolean;
}

const FormSectionLayout: React.FC<FormSectionLayoutProps> = ({
  width,
  padding,
  sections,
  formMode,
  editKey,
  isSaving,
  onEdit,
  onSave,
  dataTestId,
  elevation,
  addText,
  backgroundColor,
  disabled,
}) => {
  const defaultStyle = {
    padding: padding ?? '32px',
    width: width ?? '535px',
    backgroundColor: backgroundColor ?? '',
  };
  const disabledSectionSx = {
    pointerEvents: 'none',
    opacity: '0.3',
  } as const;

  const getSaveButton = (sectionKey: string): ReactNode => {
    return (
      <LoadingButton
        loading={isSaving}
        variant="contained"
        onClick={() => {
          onSave(sectionKey);
        }}
        disabled={disabled}
      >
        Save
      </LoadingButton>
    );
  };

  const sectionsContent = sections.map((section, idx) => {
    const isEditing = editKey === section.key || section.shouldRemainInEditMode;
    const shouldDisable = editKey && !isEditing && formMode === 'edit';
    const showSaveButton = isEditing && formMode === 'edit';
    const showEditFields = formMode === 'add' || isEditing;

    return (
      <Fragment key={section.key}>
        <Box
          sx={{ ...(shouldDisable && disabledSectionSx) }}
          data-test-id={`form-section-${section.key}`}
        >
          <Box sx={{ display: 'flex' }}>
            {section.title && (
              <Typography
                sx={{ marginBottom: showEditFields ? '24px' : '8px' }}
                variant="h4"
              >
                {section.title}
              </Typography>
            )}
            {
              <FormActionButton
                isEditing={!!isEditing}
                section={section}
                formMode={formMode}
                useAddRemove={section.allowAdd}
                hasSectionValues={section.hasSectionValues}
                onClick={() => {
                  isEditing && section.onEditCancel && section.onEditCancel();
                  onEdit && onEdit(isEditing ? null : section.key);
                }}
                dataTestId={dataTestId}
                addText={addText}
                isDisabled={disabled}
              />
            }
          </Box>
          {section.content}
          {showSaveButton && (
            <Box sx={{ marginTop: '24px' }}>{getSaveButton(section.key)}</Box>
          )}
        </Box>
        {sections.length - idx > 1 ? <Divider key={section.title} /> : null}
      </Fragment>
    );
  });
  return (
    <Paper sx={defaultStyle} elevation={elevation}>
      <Stack spacing={3}>{sectionsContent}</Stack>
    </Paper>
  );
};

export default FormSectionLayout;
